// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import type { JSX } from 'react';
import { Link } from 'react-router';
import { BookingFlow } from '../components/appointments/BookingFlow';
import { getConfig } from '../config';
import { useSchedulingEligibility } from '../hooks/useSchedulingEligibility';

/**
 * Full-page scheduling page for patient self-scheduling.
 * Shows breadcrumb navigation and the booking flow.
 * @returns The scheduling page component
 */
export function SchedulingPage(): JSX.Element {
  const { eligibility, loading, error } = useSchedulingEligibility();

  if (loading) {
    return (
      <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Stack align="center" py="xl">
            <Loader />
            <Text c="dimmed">Loading scheduling...</Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (error || !eligibility?.canSchedule) {
    return (
      <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Anchor component={Link} to="/appointments" size="sm">
            Appointments &gt;
          </Anchor>
          <Title order={2} mt="xs">
            Scheduling
          </Title>
        </Paper>
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Text c="dimmed">
            {error?.message ?? eligibility?.reason ?? 'You are not eligible to schedule at this time.'}
          </Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <Anchor component={Link} to="/appointments" size="sm">
          Appointments &gt;
        </Anchor>
        <Title order={2} mt="xs">
          Scheduling
        </Title>
      </Paper>
      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <BookingFlow eligibility={eligibility} schedulingEngine={getConfig().schedulingEngine} />
      </Paper>
    </Stack>
  );
}
